import { transport } from '@opengov/app-core/transport';

export enum AiGeneratedSummaryLength {
  SHORT = 'short',
  MEDIUM = 'medium',
  LONG = 'long'
}

async function legacyGenerateSummary({
  entityId,
  embedId,
  inputImage,
  contentLength
}: {
  entityId: number;
  inputImage: string;
  embedId?: string;
  contentLength: AiGeneratedSummaryLength;
}): Promise<string> {
  if (embedId) {
    return fetchAiGeneratedSummaryForEmbed({ entityId, inputImage, embedId, contentLength });
  }

  return fetchAiGeneratedSummaryForImageOnly({ entityId, inputImage, contentLength });
}

async function submitQuestion({
  entityId,
  userPrompt,
  contentLength,
  verbose
}: {
  entityId: number;
  userPrompt: string;
  contentLength: AiGeneratedSummaryLength;
  verbose: boolean;
}): Promise<string> {
  return fetchAnswerForPubQuestion({ entityId, userPrompt, contentLength, verbose });
}

export const AiApi = {
  generateSummary,
  legacyGenerateSummary,
  submitQuestion
};

/**
 *  Fetches
 */

type ApiResponse = {
  data?: string;
  error?: string;
};

async function fetchAiGeneratedSummaryForEmbed({
  entityId,
  embedId,
  inputImage,
  contentLength
}: {
  entityId: number;
  embedId: string;
  inputImage: string;
  contentLength: string;
}) {
  const response: ApiResponse = await transport
    .url('/api/stories/v1/data-analysis-summary')
    .json({ entityId, inputImage, embedId, contentLength })
    .post()
    .json();

  if (response?.error?.length > 0) {
    console.error(response.error);
    throw new Error(response.error);
  }

  return response.data;
}

async function fetchAiGeneratedSummaryForImageOnly({
  entityId,
  inputImage,
  contentLength
}: {
  entityId: number;
  inputImage: string;
  contentLength: string;
}) {
  const response: ApiResponse = await transport
    .url('/api/stories/v1/data-analysis-summary')
    .json({ entityId, inputImage, contentLength })
    .post()
    .json();

  if (response?.error?.length > 0) {
    console.error(response.error);
    throw new Error(response.error);
  }

  return response.data;
}

async function fetchAnswerForPubQuestion({
  entityId,
  userPrompt,
  contentLength,
  verbose
}: {
  entityId: number;
  userPrompt: string;
  contentLength: AiGeneratedSummaryLength;
  verbose: boolean;
}) {
  const response: ApiResponse = await transport
    .url('/api/stories/v1/pub-question-answer')
    .json({ userPrompt, entityId, contentLength, verbose })
    .post()
    .json();

  if (response?.error?.length > 0) {
    console.error(response.error);
    throw new Error(response.error);
  }

  return response.data;
}

async function generateSummary({
  entityId,
  embedId,
  inputImage,
  contentLength
}: {
  entityId: number;
  inputImage: string;
  embedId?: string;
  contentLength: AiGeneratedSummaryLength;
}): Promise<string> {
  try {
    if (embedId) {
      return await fetchAiGeneratedSummaryForEmbedGraphql({ entityId, inputImage, embedId, contentLength });
    }
    return await fetchAiGeneratedSummaryForImageOnlyGraphql({ entityId, inputImage, contentLength });
  } catch (error) {
    console.error('GraphQL API failed: ', error);
    throw error;
  }
}

async function fetchAiGeneratedSummaryForEmbedGraphql({
  entityId,
  embedId,
  inputImage,
  contentLength
}: {
  entityId: number;
  embedId: string;
  inputImage: string;
  contentLength: string;
}) {
  const response: ApiResponse = await transport
    .url('/api/stories/v2/data-analysis-summary')
    .json({ entityId, inputImage, embedId, contentLength })
    .post()
    .json();

  if (response?.error?.length > 0) {
    console.error(response.error);
    throw new Error(response.error);
  }

  return response.data;
}

async function fetchAiGeneratedSummaryForImageOnlyGraphql({
  entityId,
  inputImage,
  contentLength
}: {
  entityId: number;
  inputImage: string;
  contentLength: string;
}) {
  const response: ApiResponse = await transport
    .url('/api/stories/v2/data-analysis-summary')
    .json({ entityId, inputImage, contentLength })
    .post()
    .json();

  if (response?.error?.length > 0) {
    console.error(response.error);
    throw new Error(response.error);
  }

  return response.data;
}
